import React, { useState } from 'react';
import { Box, Typography, Button, Paper, TextField, MenuItem, Autocomplete, Chip, IconButton, Alert, CircularProgress } from '@mui/material';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { getCurrencySymbol } from '../../../../constants/currency';
import { OrderData, OrderPayload, ProductConfiguration } from '../types';
import { Product } from '../../order';
import { PRIORITY_OPTIONS } from '../constants';
import temporaryNumberingService from '../../../../services/temporaryNumberingService';
import ProductTable from './ProductTable';

const PAYMENT_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'partial', label: 'Partial' },
  { value: 'paid', label: 'Paid' },
  // { value: 'overdue', label: 'Overdue' }
];

interface EnhancedOrderFormProps {
  selectedCustomer: any;
  selectedSite: any;
  customers: any[];
  sites: any[];
  onCustomerChange: (customer: any) => void;
  onSiteChange: (site: any) => void;
  onProductAdded: (product: Product) => void;
  onOrderCreated: (order: OrderPayload) => void;
  configuredProducts?: Product[];
}

export const EnhancedOrderForm: React.FC<EnhancedOrderFormProps> = ({
  selectedCustomer,
  selectedSite,
  customers,
  sites,
  onCustomerChange,
  onSiteChange,
  onProductAdded,
  onOrderCreated,
  configuredProducts = []
}) => {
  const [orderData, setOrderData] = useState<OrderData>({
    amount: '',
    paymentStatus: 'pending',
    priority: 'medium',
    dueDate: '',
    notes: ''
  });

  const [products, setProducts] = useState<Product[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Combine local products with configured products
  const allProducts = [...products, ...configuredProducts];

  const handleAddProduct = () => {
    const timestamp = Date.now();
    const randomSuffix = Math.random().toString(36).substr(2, 9);
    const newProduct: Product = {
      id: `${timestamp}-${randomSuffix}`,
      product_tracking_code: `PRD-${timestamp}-${randomSuffix}`,
      order_id: '',
      order_tracking_code: '',
      customer_tracking_code: '',
      type: 'door',
      name: '', // Will be set by user in the configuration panel
      total_items: 0,
      completed_items: 0,
      quantity: 1,
      items: []
    };
    // Don't add to local products state yet - only trigger the configuration panel
    onProductAdded(newProduct);
  };

  const handleProductUpdate = (updatedProduct: Product) => {
    const updatedProducts = products.map(p => 
      p.id === updatedProduct.id ? updatedProduct : p
    );
    setProducts(updatedProducts);
    
    // Trigger a refresh of the product list
    setRefreshTrigger(prev => prev + 1);
  };


  const handleSubmitOrder = async () => {
    setIsProcessing(true);
    setError(null);
    
    try {
      // Collect all temporary numbers for finalization
      const finalizationData: Record<string, string[]> = {};
      
      allProducts.forEach(product => {
        if (product.metadata?.isAutoMode && product.metadata?.temporaryNumbers) {
          Object.entries(product.metadata.temporaryNumbers).forEach(([itemType, result]) => {
            if (result && typeof result === 'object' && 'temporary_item_numbers' in result) {
              const tempResult = result as { temporary_item_numbers: string[] };
              if (tempResult.temporary_item_numbers) {
                finalizationData[itemType] = tempResult.temporary_item_numbers;
              }
            }
          });
        }
      });

      // Finalize temporary numbers
      if (Object.keys(finalizationData).length > 0) {
        await temporaryNumberingService.finalizeMultipleTemporaryNumbers(finalizationData);
      }

      const orderPayload = {
        ...orderData,
        customer: selectedCustomer,
        site: selectedSite,
        products: allProducts
      };
      console.trace(" 🚨 handleSubmit stack");
      onOrderCreated(orderPayload);
    } catch (error) {
      console.error('Error finalizing order:', error);
      setError('Failed to finalize order. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelOrder = async () => {
    setIsProcessing(true);
    setError(null);
    
    try {
      // Collect all temporary numbers for rollback
      const rollbackRequests: Array<{ itemType: string; count: number }> = [];
      
      allProducts.forEach(product => {
        if (product.metadata?.isAutoMode && product.metadata?.temporaryNumbers) {
          Object.entries(product.metadata.temporaryNumbers).forEach(([itemType, result]) => {
            if (result && typeof result === 'object' && 'temporary_item_numbers' in result) {
              const tempResult = result as { temporary_item_numbers: string[] };
              if (tempResult.temporary_item_numbers) {
                rollbackRequests.push({
                  itemType,
                  count: tempResult.temporary_item_numbers.length
                });
              }
            }
          });
        }
      });

      // Rollback temporary numbers
      if (rollbackRequests.length > 0) {
        await temporaryNumberingService.rollbackMultipleTemporaryNumbers(rollbackRequests);
      }

      // Clear products and reset form
      setProducts([]);
      setOrderData({
        amount: '',
        paymentStatus: 'pending',
        priority: 'medium',
        dueDate: '',
        notes: ''
      });
    } catch (error) {
      console.error('Error cancelling order:', error);
      setError('Failed to cancel order. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Box sx={{ 
      display: 'grid', 
      gridTemplateRows: 'auto auto 1fr auto',
      gap: 3, 
      flex: 1,
      height: '100%'
    }}>
      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Order Details Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3}}>
          📋 Order Information
        </Typography>

        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
          gap: 2,
          mb: 3
        }}>
          <Autocomplete
            options={customers}
            getOptionLabel={(option) => option.name || ''}
            value={selectedCustomer}
            onChange={(event, newValue) => onCustomerChange(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Customer *"
                required
                size="small"
                error={!selectedCustomer}
                helperText={!selectedCustomer ? "Please select a customer" : ""}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              return (
                <Box component="li" key={key} {...otherProps}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {option.name}
                    </Typography>
                    {option.email && (
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }}
          />
          
          <Autocomplete
            options={sites?.filter(site => !selectedCustomer || site.customer?.customerId === selectedCustomer.customerId)}
            getOptionLabel={(option) => option.name || ''}
            value={selectedSite}
            onChange={(event, newValue) => onSiteChange(newValue)}
            disabled={!selectedCustomer}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Site *"
                required
                size="small"
                error={!selectedSite}
                helperText={!selectedCustomer ? "Select a customer first" : !selectedSite ? "Please select a site" : ""}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              return (
                <Box component="li" key={key} {...otherProps}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {option.name}
                    </Typography>
                    {option.address && (
                      <Typography variant="caption" color="text.secondary">
                        {option.address}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }}
          />
        </Box>
        
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: 2
        }}>
          <TextField
            label="Amount (Rs) *"
            value={orderData.amount}
            onChange={(e) => setOrderData({ ...orderData, amount: e.target.value })}
            fullWidth
            required
            size="small"
            type="text"
            inputProps={{ min: 0, step: 0.01 }}
            InputProps={{
              startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>{getCurrencySymbol()}</Typography>
            }}
          />
          
          <TextField
            select
            label="Payment Status *"
            value={orderData.paymentStatus}
            onChange={(e) => setOrderData({ ...orderData, paymentStatus: e.target.value as any })}
            fullWidth
            required
            size="small"
          >
            {PAYMENT_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          
          <TextField
            select
            label="Priority"
            value={orderData.priority}
            onChange={(e) => setOrderData({ ...orderData, priority: e.target.value as any })}
            fullWidth
            size="small"
          >
            {PRIORITY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          
          <TextField
            type="date"
            label="Due Date"
            value={orderData.dueDate}
            onChange={(e) => setOrderData({ ...orderData, dueDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
          />
        </Box>
        
        {/* <TextField
          label="Notes"
          value={orderData.notes}
          onChange={(e) => setOrderData({ ...orderData, notes: e.target.value })}
          multiline
          rows={2}
          fullWidth
          size="small"
          sx={{ mt: 2 }}
          placeholder="Add any additional notes or requirements..."
        /> */}
      </Paper>

      {/* Customer & Site Info */}
      {/* <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          👤 Customer & Site Information
        </Typography>
        
       
      </Paper> */}

      {/* Products Section */}
      <Paper sx={{ 
        p: 3, 
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        gap: 2,
        minHeight: 0
      }}>
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: 2,
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            🏗️ Products 
            <Box sx={{ 
              backgroundColor: 'primary.main', 
              color: 'white', 
              borderRadius: '50%', 
              width: 24, 
              height: 24, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 600
            }}>
              {allProducts.length}
            </Box>
          </Typography>
          
          <Button
            startIcon={<PlusIcon className="h-4 w-4" />}
            onClick={handleAddProduct}
            variant="contained"
            size="small"
            sx={{ fontSize: '0.75rem', py: 0.5 }}
            disabled={!selectedCustomer || !selectedSite}
          >
            Add Product
          </Button>
        </Box>

        {allProducts.length === 0 ? (
          <Box sx={{ 
            display: 'grid',
            placeItems: 'center',
            py: 4, 
            color: 'text.secondary',
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            backgroundColor: 'action.hover',
            minHeight: '200px'
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {!selectedCustomer || !selectedSite 
                  ? "Please select customer and site first" 
                  : "No products added yet"
                }
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {!selectedCustomer || !selectedSite 
                  ? "Customer and site selection required" 
                  : "Click 'Add Product' to get started"
                }
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ 
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {allProducts.map((product) => (
              <Paper 
                key={`${product.id}-${refreshTrigger}`} 
                variant="outlined" 
                sx={{ 
                  p: 1.5,
                  position: 'relative',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: 2,
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                {/* Close Button */}
                <IconButton
                  size="small"
                  onClick={() => {
                    const updatedProducts = products.filter(p => p.id !== product.id);
                    setProducts(updatedProducts);
                  }}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    width: 24,
                    height: 24,
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'error.main',
                      backgroundColor: 'error.light'
                    }
                  }}
                >
                  <XMarkIcon className="h-4 w-4" />
                </IconButton>

                {/* Header: Label - Location */}
                <Box sx={{ pr: 3, mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    fontSize: '0.875rem',
                    lineHeight: 1.2
                  }}>
                    {product.label || 'Unlabeled Product'}
                  </Typography>
                  {product.location && (
                    <Typography variant="caption" sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      display: 'block',
                      mt: 0.25
                    }}>
                      {product.location}
                    </Typography>
                  )}
                </Box>

                {/* Product Type Chip */}
                <Box sx={{ mb: 1.5 }}>
                  <Chip
                    label={product.type?.charAt(0).toUpperCase() + product.type?.slice(1) || 'Unknown'}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: '0.7rem',
                      height: 20,
                      backgroundColor: 'primary.light',
                      color: 'primary.contrastText',
                      border: 'none',
                      '& .MuiChip-label': {
                        px: 1
                      }
                    }}
                  />
                </Box>

                {/* Generated Numbers Display */}
                {product.metadata?.isAutoMode && product.metadata?.temporaryNumbers && (
                  <Box sx={{ mb: 2, p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ 
                      display: 'block',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: 'success.dark',
                      mb: 0.5
                    }}>
                      📋 Generated Numbers:
                    </Typography>
                    {Object.entries(product.metadata.temporaryNumbers).map(([itemType, result]) => {
                      const tempResult = result as { temporary_item_numbers?: string[] };
                      
                      // Convert full names to abbreviated display names
                      const convertToDisplayName = (fullName: string) => {
                        if (fullName.startsWith('Frame-')) return fullName.replace('Frame-', 'F-');
                        if (fullName.startsWith('Panel-')) return fullName.replace('Panel-', 'P-');
                        if (fullName.startsWith('Panel Window-')) return fullName.replace('Panel Window-', 'PW-');
                        if (fullName.startsWith('Net Fixing-')) return fullName.replace('Net Fixing-', 'N-');
                        return fullName;
                      };
                      
                      const displayNumbers = tempResult.temporary_item_numbers?.map(convertToDisplayName).join(', ') || 'None';
                      
                      return (
                        <Typography key={itemType} variant="caption" sx={{ 
                          display: 'block',
                          fontSize: '0.65rem',
                          color: 'success.dark',
                          fontFamily: 'monospace'
                        }}>
                          {itemType}: {displayNumbers}
                        </Typography>
                      );
                    })}
                  </Box>
                )}

                {/* Stats Row */}
                <Box sx={{ 
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 1,
                  mt: 'auto'
                }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ 
                      display: 'block',
                      fontSize: '0.7rem',
                      fontWeight: 500
                    }}>
                      Total
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      fontWeight: 600,
                      color: 'primary.main',
                      fontSize: '0.875rem'
                    }}>
                      {product.total_items || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ 
                      display: 'block',
                      fontSize: '0.7rem',
                      fontWeight: 500
                    }}>
                      Completed
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      fontWeight: 600,
                      color: 'success.main',
                      fontSize: '0.875rem'
                    }}>
                      {product.completed_items || 0}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Paper>

      {/* Submit Section */}
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
        gap: 2, 
        pt: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        alignItems: 'center'
      }}>
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(120px, auto))' },
          gap: 1,
          alignItems: 'center',
          justifyItems: { xs: 'center', sm: 'start' }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Amount:
            </Typography>
            <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
              ${orderData.amount || '0.00'}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Products:
            </Typography>
            <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
              {allProducts.length}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Items:
            </Typography>
            <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
              {products.reduce((sum, product) => sum + (product.total_items || 0), 0)}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Completed:
            </Typography>
            <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
              {products.reduce((sum, product) => sum + (product.completed_items || 0), 0)}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: 'auto auto auto',
          gap: 2,
          justifySelf: { xs: 'center', sm: 'end' }
        }}>
          <Button 
            variant="outlined" 
            size="small"
            onClick={handleCancelOrder}
            disabled={isProcessing || allProducts.length === 0}
            sx={{ 
              fontSize: '0.75rem',
              minWidth: '100px'
            }}
          >
            {isProcessing ? <CircularProgress size={16} /> : 'Cancel Order'}
          </Button>
          <Button 
            variant="outlined" 
            size="small"
            sx={{ 
              fontSize: '0.75rem',
              minWidth: '100px'
            }}
          >
            Save Draft
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSubmitOrder}
            disabled={!selectedCustomer || !selectedSite || allProducts.length === 0 || !orderData.amount || !orderData.paymentStatus || isProcessing}
            sx={{ 
              fontSize: '0.75rem',
              minWidth: '120px',
              fontWeight: 600
            }}
          >
            {isProcessing ? <CircularProgress size={16} /> : 'Create Order'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

// export default EnhancedOrderForm;
